import React from 'react';
import classNames from 'classnames';
import { LikeIcon } from 'icons';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { globalConst, useInteractionHandler, UserInteraction } from 'modules/global';
import { UniversityTypes } from 'modules/universities/models';

import { Visible } from 'components';

interface IProps {
  id?: string;
  image: string;
  logo: string;
  universityName: string;
  desc: string;
  directionCount: string;
  type: string;
  slug?: string;
  isRecommended?: boolean;
}

const RightSideCard: React.FC<IProps> = ({
  id,
  image,
  logo,
  universityName,
  desc,
  directionCount,
  type,
  slug,
  isRecommended
}) => {
  const t = useTranslations();
  const { onInteractionClick } = useInteractionHandler(id);

  return (
    <Link
      href={`${type === UniversityTypes.PRIVATE ? globalConst.pageNames.PRIVATE_UNIVERSITIES : globalConst.pageNames.UNIVERSITIES}/${slug}`}
      onClick={onInteractionClick(UserInteraction.CARD_CLICK)}
    >
      <div className="flex max-h-80 min-h-80 flex-col rounded-xl bg-white shadow-sm hover:text-green-500">
        {image ? (
          <div className="relative max-h-40 min-h-40 w-full overflow-hidden rounded-lg">
            {isRecommended && (
              <div className="absolute -right-3 z-10 flex items-center gap-1 rounded-full bg-orange-500 pl-2 pr-5 text-sm text-white">
                <LikeIcon /> {t('tavsiya_etiladi')}
              </div>
            )}
            <Image
              fill
              className=" rounded-t-xl object-cover"
              src={image?.trimEnd() || '/images/default-no-image.webp'}
              alt={universityName}
              title="Universitet rasmi"
            />
          </div>
        ) : (
          <div className="relative flex max-h-40 min-h-40  w-full items-center justify-center rounded-t-xl bg-gray-50 overflow-hidden rounded-lg">
            {isRecommended && (
              <div className="absolute -right-3 top-0 z-10 flex items-center gap-1 rounded-full bg-orange-500 pl-2 pr-5 text-sm text-white">
                <LikeIcon /> {t('tavsiya_etiladi')}
              </div>
            )}
            <Image
              width={70}
              height={70}
              className=" object-cover"
              src="/images/default-no-image.webp"
              alt={universityName}
              title="Universitet rasmi"
            />
          </div>
        )}

        <div className="flex flex-1 flex-col p-3">
          <div className="flex items-center gap-2">
            <div className="relative flex min-h-10 min-w-10 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-50">
              <Image
                src={logo || '/images/default-no-logo.webp'}
                alt={universityName.slice(0, 1)}
                width={logo ? 40 : 23}
                height={logo ? 40 : 23}
                className="object-contain"
                title="Universitet logosi"
              />
            </div>
            <div className="line-clamp-2 text-base font-medium">{universityName}</div>
          </div>
          <div className="mt-4 line-clamp-2 text-xs text-gray-500">{desc ?? t('tavsif_mavjud_emas')}</div>
          <div className="mt-auto flex items-center justify-between">
            <span className="text-sm text-blue-600">
              {t('Yonalishlar')} - {directionCount}
            </span>
            <span
              className={classNames('rounded-full px-2 py-1 text-xs', {
                'bg-teal-50 text-teal-500': type === UniversityTypes.PUBLIC,
                'bg-orange-50 text-orange-500': type === UniversityTypes.PRIVATE
              })}
            >
              <Visible
                if={type === UniversityTypes.PUBLIC}
                else={<Visible if={type === UniversityTypes.PRIVATE}>{t('xususiy')}</Visible>}
              >
                {t('davlat')}
              </Visible>
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default RightSideCard;
